import type { PICKER_MODE } from '@shared/common/constants/PickerMode';

export interface Common {
  id?: string;
  classes?: string;
  children?: any;
}

export interface ChartPieItem {
  name: string;
  value: number;
  colour: string;
}

export interface ChartRatingItem {
  value: number;
  icon: JSX.Element;
}

export interface ChartLineItem {
  name: string;
  value: number;
}

export interface FormField {
  value: any;
  errorMessage: string;
  required: boolean;
  isValid?: boolean;
}

export type FormKeys<FormField extends Record<string, any>> = {
  [K in keyof FormField]: FormField[K];
};

export enum LLM {
  GPT4 = 2,
  GPT4o = 3,
  GPT4o_MINI = 4,
  GPT5 = 5,
  GPT5_MINI = 6,
  GPT5_NANO = 7,
  GPT5_1 = 8,
  GPT4_1 = 9,
  GPT4_1_MINI = 10,
  GPT4_1_NANO = 11,
  GPT5_2 = 12,
  GPT5_4 = 14,
  GPT5_5 = 15,
  GPT5_6_SOL = 16,
  GPT6_ASTRA = 17,
  GPT5_6_TERRA = 18,
  GPT5_6_LUNA = 19,
  CLAUDE_SONNET_5 = 20,
  DEEPSEEK_V4_PRO = 21,
  COHERE_COMMAND_A_PLUS = 22,
  GROK_4_1_FAST_REASONING = 23,
  FW_KIMI_K3 = 24,
  GPT6_1_SOL = 25,
  GPT6_SOL = 26,
  GPT6_LUNA = 27,
}

export enum DeployType {
  GenericUser = 1,
  WithoutSSO = 2,
  WithSSO = 3,
}

export enum Personas {
  Electricians = 'electrician',
  ElectriciansInspectors = 'electrician-inspector',
  ElectricalEngineers = 'electrical-engineer',
  ElectricalDesign = 'electrical-designer',
  EstimationSpecialists = 'estimation-specialist',
  Apprentices = 'apprentice',
}

export enum PromptStyles {
  'electrician' = 'Tone & Style: Provide a practical, straightforward answer that gets right to the point, with a focus on the application. Include relevant clause numbers from the appropriate standards and legislation.',
  'apprentice' = 'Tone & Style: Give a clear, encouraging explanation that simplifies the concept for easy understanding, ideally using analogies. Briefly reference clause numbers from the appropriate standards and legislation to support your explanation.',
  'electrical-designer' = "Tone & Style: Deliver a detailed, comprehensive response akin to a design/estimation specialist's precise approach, connecting practical guidelines with theoretical principles. Ensure the principles are applied practically, citing specific clause numbers from the appropriate standards and legislation.",
  'electrical-engineer' = "Tone & Style: Craft a technically detailed response that reflects an engineer's analytical approach. The style should be professional, including clause numbers from the appropriate standards and legislation where relevant.",
  'electrician-inspector' = 'Tone & Style: Provide a formal, regulatory-focused answer, that integrates clause numbers from the appropriate standards and legislation. The answer needs to convey an in-depth understanding of technical standards and safety requirements applicable to electrical inspections.',
  'estimation-specialist' = "Tone & Style: Deliver a detailed, comprehensive response akin to a design/estimation specialist's precise approach, connecting practical guidelines with theoretical principles. Ensure the principles are applied practically, citing specific clause numbers from the appropriate standards and legislation.",
}

export enum BillingCycleEnum {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annual = 'Annual',
}

export enum TransactionModel {
  IncludedMonthlyAllowance = 'IncludedMonthlyAllowance',
  PrepaidBundle = 'PrepaidBundle',
}

export enum ClientType {
  B2B = 'B2B',
  B2C = 'B2C',
}

export enum DateRangeEnum {
  Select = '',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  LastTwoMonths = 'Last Two Months',
  LastYear = 'Last Year',
  Custom = 'Custom',
}

export enum FontFamily {
  Roboto = 'Roboto',
  OpenSans = 'OpenSans',
  Lato = 'Lato',
  Montserrat = 'Montserrat',
  Poppins = 'Poppins',
  SourceSansPro = 'SourceSansPro',
  Raleway = 'Raleway',
  NotoSans = 'NotoSans',
  Merriweather = 'Merriweather',
  Ubuntu = 'Ubuntu',
  RobotoCondensed = 'RobotoCondensed',
  PlayfairDisplay = 'PlayfairDisplay',
  Nunito = 'Nunito',
  FiraSans = 'FiraSans',
  Arvo = 'Arvo',
}

export enum UserImportStatus {
  Success = 'Success',
  Failed = 'Failed',
  Existing = 'Existing',
}

export enum FeedbackFormStatus {
  Init,
  Loading,
  Success,
}

export enum SearchTablePage {
  Insights = 'Insights',
  DeleteInsight = 'DeleteInsight',
  Documents = 'Documents',
  Organisations = 'Organisations',
  Users = 'Users',
  Subscriptions = 'Subscriptions',
  Administrations = 'Administrations',
  Evaluations = 'Evaluations',
  EditUserOrganisation = 'EditUserOrganisation',
  EditUserOrganisationModal = 'EditUserOrganisationModal',
  EditUserInsights = 'EditUserInsights',
  AddEvaluationFiles = 'AddEvaluationFiles',
  AddEvaluationInsights = 'AddEvaluationInsights',
  OrganisationAssignUsers = 'OrganisationAssignUsers',
  InsightsDocuments = 'InsightsDocuments',
  EditOrganisationInsights = 'EditOrganisationInsights',
  AddOrganisationBillings = 'AddOrganisationBillings',
  AddUserOrganisations = 'AddUserOrganisations',
  AddUserInsights = 'AddUserInsights',
  OrganisationAssignUsersModal = 'OrganisationAssignUsersModal',
  EditInsightFilterModal = 'EditInsightFilterModal',
  EditInsightAddFilter = 'EditInsightAddFilter',
  EditOrganisationBillings = 'EditOrganisationBillings',
  EditInsightOrganisations = 'EditInsightOrganisations',
  EditOrganisationImportedUsers = 'EditOrganisationImportedUsers',
  EditDictionary = 'EditDictionary',
  AddDictionary = 'AddDictionary',
  Dictionaries = 'Dictionaries',
  AddUserOrganisationsModal = 'AddUserOrganisationsModal',
  AddEvaluationQandA = 'AddEvaluationQandA',
  AddOrganisationInsight = 'AddOrganisationInsight',
  AssignInsightModal = 'AssignInsightModal',
  InsightsDictionaryModal = 'InsightsDictionaryModal',
  DocumentsStatus = 'DocumentsStatus',
  DocumentsItemStatus = 'DocumentsItemStatus',
  OrphandedDocuments = 'OrphandedDocuments',
  Permissions = 'Permissions',
  None = 'None',
}

export enum AgentType {
  PreRAGAgent = "PreRAGAgent",
  PostRAGAgent = "PostRAGAgent",
}

export type PickerMode = (typeof PICKER_MODE)[keyof typeof PICKER_MODE];
